import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { rentalsAPI, productsAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const Rentals = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const [rentals, setRentals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(null);
  const [returnData, setReturnData] = useState({
    late_fee: 0, damage_fee: 0, notes: '', condition: 'good',
  });
  const [formData, setFormData] = useState({
    product_id: '', customer_name: '', customer_phone: '', rental_start: '', rental_end: '', deposit_amount: 0,
  });

  // Helper functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (rental) => {
    if (rental.return_status === 'returned') return 'badge-success';
    if (rental.is_overdue) return 'badge-danger';
    return 'badge-warning';
  };

  const getStatusLabel = (rental) => {
    if (rental.return_status === 'returned') return 'Returned';
    if (rental.is_overdue) return 'Overdue';
    return 'Active';
  };

  const renderIcon = (iconName) => {
    const icons = {
      calendar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      clock: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      alertCircle: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      ),
      checkCircle: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      repeat: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  // Calculate stats
  const stats = {
    total: rentals.length,
    active: rentals.filter(r => r.return_status === 'pending' && !r.is_overdue).length,
    overdue: rentals.filter(r => r.is_overdue).length,
    returned: rentals.filter(r => r.return_status === 'returned').length,
    totalRevenue: rentals.reduce((sum, r) => sum + parseFloat(r.total_amount || 0), 0),
  };

  const loadRentals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await rentalsAPI.list(selectedStore.id);
      setRentals(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load rentals'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list(selectedStore.id, { rentable_only: true });
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load products');
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadRentals();
      loadProducts();
    }
  }, [selectedStore, loadRentals, loadProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await rentalsAPI.create({ ...formData, store_id: selectedStore.id });
      showToast('Rental created - track returns easily', 'success');
      loadRentals();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to create rental'));
    }
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    try {
      await rentalsAPI.update(showReturnModal.id, {
        return_status: 'returned',
        late_fee: parseFloat(returnData.late_fee) || 0,
        damage_fee: parseFloat(returnData.damage_fee) || 0,
        return_notes: returnData.notes,
        return_condition: returnData.condition,
      });
      showToast('Rental returned successfully', 'success');
      loadRentals();
      setShowReturnModal(null);
      setReturnData({ late_fee: 0, damage_fee: 0, notes: '', condition: 'good' });
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to return rental'));
    }
  };

  const resetForm = () => {
    setFormData({ product_id: '', customer_name: '', customer_phone: '', rental_start: '', rental_end: '', deposit_amount: 0 });
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading rentals...</span></div>;
  }

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Rentals</h1>
          <p className="page-subtitle">Manage product rentals and track return status</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Rental'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Stats Row */}
      <div className="page-stats-row">
        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--primary)' }}>
            {renderIcon('calendar')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Total Rentals</div>
            <div className="page-stat-value">{formatNumber(stats.total)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--warning)' }}>
            {renderIcon('clock')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Active</div>
            <div className="page-stat-value">{formatNumber(stats.active)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--danger)' }}>
            {renderIcon('alertCircle')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Overdue</div>
            <div className="page-stat-value">{formatNumber(stats.overdue)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--success)' }}>
            {renderIcon('checkCircle')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Returned</div>
            <div className="page-stat-value">{formatNumber(stats.returned)}</div>
          </div>
        </div>
      </div>

      {/* Create Rental Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Create Rental Booking</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Product *</label>
                  <select
                    className="form-select"
                    value={formData.product_id}
                    onChange={(e) => setFormData({ ...formData, product_id: parseInt(e.target.value) })}
                    required
                  >
                    <option value="">Select Product</option>
                    {products.filter(p => p.is_rentable).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} - ${parseFloat(p.rental_price || 0).toFixed(2)}/day
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Deposit Amount</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.deposit_amount}
                    min="0"
                    step="0.01"
                    onChange={(e) => setFormData({ ...formData, deposit_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.customer_name}
                    required
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Phone *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.customer_phone}
                    required
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Start Date *</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.rental_start}
                    required
                    onChange={(e) => setFormData({ ...formData, rental_start: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date *</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.rental_end}
                    required
                    onChange={(e) => setFormData({ ...formData, rental_end: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Create Booking</button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Table Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">All Rentals ({rentals.length})</h3>
          <div className="card-header-actions">
            <button className="btn btn-sm btn-secondary">Export</button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {rentals.length === 0 ? (
            <div className="page-empty-state">
              <div className="page-empty-icon">
                {renderIcon('repeat')}
              </div>
              <div className="page-empty-title">No active rentals</div>
              <p className="page-empty-text">
                Rental records will appear here once you start renting items to customers
              </p>
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => setShowForm(true)}
              >
                Create First Rental
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Customer</th>
                      <th>Rental Period</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th>Status</th>
                      <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rentals.map(rental => (
                      <tr key={rental.id}>
                        <td className="font-medium">{rental.product_name || `Product #${rental.product_id}`}</td>
                        <td>
                          {rental.customer_name}
                          <br />
                          <small className="text-muted">{rental.customer_phone}</small>
                        </td>
                        <td style={{ fontSize: '0.875rem' }}>
                          {formatDate(rental.rental_start)} - {formatDate(rental.rental_end)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(rental.total_amount)}
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(rental)}`}>
                            {getStatusLabel(rental)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {rental.return_status === 'pending' && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => setShowReturnModal(rental)}
                            >
                              Mark Returned
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards hidden-desktop">
                {rentals.map(rental => (
                  <div className="mobile-card" key={rental.id}>
                    <div className="mobile-card-header">
                      <div>
                        <div className="mobile-card-title">
                          {rental.product_name || `Product #${rental.product_id}`}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>{rental.customer_name}</div>
                      </div>
                      <span className={`badge ${getStatusBadge(rental)}`}>
                        {getStatusLabel(rental)}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Period</span>
                      <span style={{ fontSize: '0.875rem' }}>
                        {formatDate(rental.rental_start)} - {formatDate(rental.rental_end)}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Phone</span>
                      <span>{rental.customer_phone}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Total</span>
                      <span className="font-bold">{formatCurrency(rental.total_amount)}</span>
                    </div>
                    {rental.return_status === 'pending' && (
                      <div className="mobile-card-actions">
                        <button className="btn btn-sm btn-success" onClick={() => setShowReturnModal(rental)}>
                          Mark Returned
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Return Modal */}
      {showReturnModal && (
        <div className="modal-overlay" onClick={() => setShowReturnModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Process Return</h3>
              <button className="modal-close" onClick={() => setShowReturnModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleReturn}>
                <div className="form-group">
                  <label className="form-label">Condition</label>
                  <select
                    className="form-select"
                    value={returnData.condition}
                    onChange={(e) => setReturnData({ ...returnData, condition: e.target.value })}
                  >
                    <option value="good">Good Condition</option>
                    <option value="damaged">Damaged</option>
                    <option value="missing">Missing Parts</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Late Fee ($)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={returnData.late_fee}
                    step="0.01"
                    min="0"
                    onChange={(e) => setReturnData({ ...returnData, late_fee: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Damage Fee ($)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={returnData.damage_fee}
                    step="0.01"
                    min="0"
                    onChange={(e) => setReturnData({ ...returnData, damage_fee: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-textarea"
                    value={returnData.notes}
                    rows={3}
                    placeholder="Additional notes about the return..."
                    onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Confirm Return</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowReturnModal(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rentals;
